class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();

    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      if (this.shouldPreventSubmit(event)) return;
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    if (facetForm) {
      facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
    }

    /** Appelé par facet-range pour forcer la mise à jour de l’URL (slider sans dépendre à la propagation). */
    this.triggerFiltersUpdate = this.debouncedOnSubmit.bind(this);

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');

    document.getElementById('ProductGridContainer').querySelector('.collection-grid-container').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) {
      FacetFiltersForm.updateURLHash(searchParams);
      // Afficher tout de suite le chip sillage si la sélection a changé (avant la fin du fetch)
      FacetFiltersForm.injectSillageActiveFacet();
    }
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        const searchParams = FacetFiltersForm.getSearchParamsFromUrl(url);
        FacetFiltersForm.renderFilters(html, event, searchParams);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        // Après remplacement du ProductGridContainer (qui contient .active-facets), réinjecter le chip sillage
        FacetFiltersForm.injectSillageActiveFacet();
        if (document.getElementById('SortBy')) {
          document.getElementById('SortBy').dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);

        setTimeout(() => {
          FacetFiltersForm.initializeShowMore();
        }, 0);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const { html, url } = FacetFiltersForm.filterData.find(filterDataUrl);
    const searchParams = FacetFiltersForm.getSearchParamsFromUrl(url);
    FacetFiltersForm.renderFilters(html, event, searchParams);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    FacetFiltersForm.injectSillageActiveFacet();
    if (document.getElementById('SortBy')) {
      document.getElementById('SortBy').dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);

    setTimeout(() => {
      FacetFiltersForm.initializeShowMore();
    }, 0);
  }

  /** Indique si le tiroir filtres (drawer) était ouvert avant un re-render AJAX. */
  static wasFacetsFilterDrawerOpen() {
    const details = document.querySelector('.facets__drawer details');
    if (!details) return false;
    return details.hasAttribute('open') || details.classList.contains('menu-opening');
  }

  /**
   * Après remplacement du HTML, le nouveau <details> est fermé : on rouvre le tiroir si l’utilisateur l’avait laissé ouvert.
   * Sans cela, chaque mise à jour de grille (filtre, tri) referme le drawer.
   */
  static restoreFacetsFilterDrawerIfWasOpen(wasOpen) {
    if (!wasOpen) return;
    requestAnimationFrame(() => {
      const menuDrawer = document.querySelector('menu-drawer.facets__drawer');
      const details = menuDrawer?.querySelector('details');
      if (!menuDrawer || !details || typeof menuDrawer.toggleDrawer !== 'function') return;
      if (!details.hasAttribute('open')) {
        menuDrawer.toggleDrawer();
      }
    });
  }

  static renderProductGridContainer(html) {
    const keepDrawerOpen = FacetFiltersForm.wasFacetsFilterDrawerOpen();

    const live = document.getElementById('ProductGridContainer');
    if (!live) return;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fetched = doc.getElementById('ProductGridContainer');
    if (!fetched) return;

    live.innerHTML = fetched.innerHTML;

    live.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
    });

    FacetFiltersForm.restoreFacetsFilterDrawerIfWasOpen(keepDrawerOpen);
  }

  static renderProductCount(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const countElement = parsedHTML.getElementById('ProductCount');

    if (!countElement) {
      return;
    }

    const count = countElement.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');

    if (container) {
      container.innerHTML = count;
      container.classList.remove('loading');
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
  }

  /** Extrait les paramètres de recherche depuis l’URL de section (sans section_id) pour préserver les filtres (ex. sillage). */
  static getSearchParamsFromUrl(url) {
    if (!url || !url.includes('?')) return '';
    const query = url.slice(url.indexOf('?') + 1);
    const params = new URLSearchParams(query);
    params.delete('section_id');
    return params.toString();
  }

  /** Restaure les valeurs des facet-range (ex. sillage) depuis les searchParams après re-render. */
  static restoreFacetRangesFromSearchParams(searchParams) {
    if (!searchParams) return;
    const params = new URLSearchParams(searchParams);
    document.querySelectorAll('facet-range').forEach((facetRange) => {
      const paramGte = facetRange.getAttribute('data-param-gte');
      const paramLte = facetRange.getAttribute('data-param-lte');
      if (!paramGte || !paramLte) return;
      const gte = params.get(paramGte);
      const lte = params.get(paramLte);
      if (gte === null || lte === null) return;
      const minVal = parseFloat(gte);
      const maxVal = parseFloat(lte);
      if (Number.isNaN(minVal) || Number.isNaN(maxVal)) return;
      const priceRangeMin = facetRange.querySelector('input[type="range"]:first-of-type');
      const priceRangeMax = facetRange.querySelector('input[type="range"]:last-of-type');
      const priceInputMin = facetRange.querySelector(`input[name="${paramGte}"]`);
      const priceInputMax = facetRange.querySelector(`input[name="${paramLte}"]`);
      if (!priceRangeMin || !priceRangeMax || !priceInputMin || !priceInputMax) return;
      const rangeMin = parseFloat(priceRangeMin.getAttribute('min')) || 0;
      const rangeMax = parseFloat(priceRangeMax.getAttribute('max')) || 100;
      const span = rangeMax - rangeMin;
      if (span <= 0) return;
      const clampedMin = Math.max(rangeMin, Math.min(minVal, rangeMax));
      const clampedMax = Math.max(rangeMin, Math.min(maxVal, rangeMax));
      priceInputMin.value = clampedMin;
      priceInputMax.value = clampedMax;
      priceRangeMin.value = clampedMin;
      priceRangeMax.value = clampedMax;
      const rangeGroup = facetRange.querySelector('.range-group');
      if (rangeGroup) {
        rangeGroup.style.setProperty('--range-min', `${((clampedMin - rangeMin) / span) * 100}%`);
        rangeGroup.style.setProperty('--range-max', `${((clampedMax - rangeMin) / span) * 100}%`);
      }
    });
  }

  static renderFilters(html, event, searchParams) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter');
    const facetDetailsElementsFromDom = document.querySelectorAll('facet-filters-form form .js-filter');

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };


    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);
    facetsToRender.forEach((elementToRender, index) => {
        const currentElement = document.getElementById(elementToRender.id);
        // Element already rendered in the DOM so just update the innerHTML
        if (currentElement) {
          document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
        } else {
          // Trouver un ancrage dans le DOM pour insérer le nouveau facet (l'élément précédent peut avoir été supprimé)
          let anchor = null;
          if (index > 0) {
            const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
            if (elementToRender.className === previousElementClassName) {
              anchor = document.getElementById(previousElementId);
            }
          }
          // Si l'élément précédent a été retiré du DOM, prendre le dernier facet encore présent avant cet index
          if (!anchor && index > 0) {
            for (let i = index - 1; i >= 0; i--) {
              const candidate = document.getElementById(facetsToRender[i].id);
              if (candidate) {
                anchor = candidate;
                break;
              }
            }
          }
          // Fallback : premier .js-filter du formulaire ou le formulaire lui-même
          if (!anchor) {
            anchor = document.querySelector('facet-filters-form form .js-filter') || document.getElementById('FacetFiltersForm');
          }
          if (anchor) {
            anchor.after(elementToRender);
          }
        }
      });


    FacetFiltersForm.renderActiveFacets(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementToActivate = newFacetDetailsElement.querySelector('.facets__summary');

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }

    // Après re-render, restaurer les valeurs des sliders type facet-range (ex. sillage) depuis l’URL
    FacetFiltersForm.restoreFacetRangesFromSearchParams(searchParams);
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  /**
   * Affiche le filtre sillage comme active-facet (chip) quand gte/lte sont dans l’URL.
   * Le lien retire les paramètres sillage de l’URL. Liquid n’ayant pas accès aux query params, on injecte en JS.
   */
  static injectSillageActiveFacet() {
    const paramGte = 'filter.p.m.custom.sillage.gte';
    const paramLte = 'filter.p.m.custom.sillage.lte';
    const container = document.querySelector('.active-facets');
    const existing = document.getElementById('active-facet-sillage');
    if (existing) existing.remove();

    const params = new URLSearchParams(window.location.search);
    const gte = params.get(paramGte);
    const lte = params.get(paramLte);
    if (!gte || !lte || !container) return;

    const removeParams = new URLSearchParams(window.location.search);
    removeParams.delete(paramGte);
    removeParams.delete(paramLte);
    const removeUrl = `${window.location.pathname}${removeParams.toString() ? '?' + removeParams.toString() : ''}`;

    const label = `Sillage : ${gte} - ${lte}`;
    const closeIcon = container.querySelector('.active-facets__button-link .svg-wrapper');
    const closeHtml = closeIcon ? closeIcon.innerHTML : '<span aria-hidden="true">×</span>';

    const buttonHtml = `
      <facet-remove>
        <a href="${removeUrl}" class="active-facets__button active-facets__button-link">
          <span class="active-facets__button-text active-facets__button--price-range">
            ${label}
            <span class="svg-wrapper">${closeHtml}</span>
          </span>
        </a>
      </facet-remove>
    `;

    const wrapper = container.querySelector('.swiper-wrapper');
    const chip = document.createElement('div');
    chip.id = 'active-facet-sillage';
    chip.className = wrapper ? 'swiper-slide' : 'active-facets__button';
    chip.innerHTML = wrapper ? `<div class="active-facets__button">${buttonHtml}</div>` : buttonHtml;

    if (wrapper) {
      wrapper.insertBefore(chip, wrapper.firstChild);
    } else {
      container.insertBefore(chip, container.firstChild);
    }
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();

    const params = new URLSearchParams();

    const currentUrl = new URL(window.location.href);
    const sortParam = currentUrl.searchParams.get('sort_by');
    if (sortParam) {
      params.set('sort_by', sortParam);
    }

    // Preserve the 'q' parameter for search pages
    const qParam = currentUrl.searchParams.get('q');
    if (qParam) {
      params.set('q', qParam);
    }

    const sortBySelect = document.getElementById('SortBy');
    if (sortBySelect && sortBySelect.value) {
      params.set('sort_by', sortBySelect.value);
    }

    let sortFilterForms = Array.from(document.querySelectorAll('facet-filters-form form'));

    sortFilterForms = sortFilterForms.filter(form => {
      const hasFilterCheckboxes = form.querySelector('input[type="checkbox"][name^="filter"]');
      const hasFacetRange = form.querySelector('facet-range');
      // Inclure les formulaires avec filtres liste OU avec un slider type facet-range (ex. sillage)
      return hasFilterCheckboxes !== null || hasFacetRange !== null;
    });

    const allFilterCheckboxes = {};
    sortFilterForms.forEach(form => {
      form.querySelectorAll('input[type="checkbox"]').forEach(input => {
        const key = `${input.name}|${input.value}`;
        if (!allFilterCheckboxes[key]) {
          allFilterCheckboxes[key] = [];
        }
        allFilterCheckboxes[key].push(input);
      });
    });

    Object.values(allFilterCheckboxes).forEach(inputs => {
      if (inputs.length > 1) {
        const triggerForm = event.target ? event.target.closest('form') : null;
        const primaryInput = inputs.find(input => input.closest('form') === triggerForm) || inputs[0];
        inputs.forEach(input => {
          if (input !== primaryInput) {
            input.checked = primaryInput.checked;
          }
        });
      }
    });

    if (window.matchMedia('(max-width:990px)').matches) {
      sortFilterForms = sortFilterForms.filter(form => !form.closest('.collection__facets--sidebar'));
    }

    // Si le changement vient d’un champ (ex. slider sillage), ne collecter que depuis ce formulaire
    // pour éviter qu’un second formulaire (drawer/sidebar) écrase les valeurs.
    const triggerForm = event && event.target && event.target.closest ? event.target.closest('form') : null;
    const formsToProcess = triggerForm != null
      ? sortFilterForms.filter((form) => form === triggerForm)
      : sortFilterForms;

    formsToProcess.forEach(form => {
      const checkboxInputs = form.querySelectorAll('input[type="checkbox"]');
      checkboxInputs.forEach(input => {
        if (input.checked) {
          params.append(input.name, input.value);
        }
      });

      const otherInputs = form.querySelectorAll('input:not([type="checkbox"])');
      otherInputs.forEach(input => {
        if (input.value && input.name !== 'sort_by' && input.name) {

          // These two conditions for prevent price facet to be submitted if it is at the minimum or maximum value
          if (input.name === 'filter.v.price.gte') {
            const min = input.getAttribute('min');
            if (min !== null && Number(input.value) === Number(min)) return;
          }

          if (input.name === 'filter.v.price.lte') {
            const max = input.getAttribute('max');
            if (max !== null && Number(input.value) === Number(max)) return;
          }

          params.set(input.name, input.value);
        }
      });

      const selectElements = form.querySelectorAll('select');
      selectElements.forEach(select => {
        if (select.value && select.name === 'sort_by') {
          params.set('sort_by', select.value);
        }
      });
    });

    // Inclure les paramètres des facet-range (ex. sillage) depuis le même formulaire que ci‑dessus.
    document.querySelectorAll('facet-range').forEach((facetRange) => {
      if (triggerForm != null && facetRange.closest('form') !== triggerForm) return;
      const paramGte = facetRange.getAttribute('data-param-gte');
      const paramLte = facetRange.getAttribute('data-param-lte');
      if (!paramGte || !paramLte) return;
      const inputMin = facetRange.querySelector(`input[name="${paramGte}"]`);
      const inputMax = facetRange.querySelector(`input[name="${paramLte}"]`);
      if (inputMin && inputMax && inputMin.value !== '' && inputMax.value !== '') {
        params.set(paramGte, inputMin.value);
        params.set(paramLte, inputMax.value);
      }
    });

    this.onSubmitForm(params.toString(), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    let url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);

    // Preserve the 'q' parameter for search pages
    const currentUrl = new URL(window.location.href);
    const qParam = currentUrl.searchParams.get('q');
    if (qParam) {
      const params = new URLSearchParams(url);
      params.set('q', qParam);
      url = params.toString();
    }

    FacetFiltersForm.renderPage(url);
  }

  shouldPreventSubmit(event) {
    const minInput = event?.target?.form?.querySelector('input[name="filter.v.price.gte"]');
    const maxInput = event?.target?.form?.querySelector('input[name="filter.v.price.lte"]');

    if (minInput && maxInput) {
      const min = parseFloat(minInput.value);
      const max = parseFloat(maxInput.value);

      if (!isNaN(min) && !isNaN(max) && min >= max) {
        return true;
      }
    }

    return false;
  }

  static initializeShowMore() {
    document.querySelectorAll('.js-filter[data-show-more-count]').forEach(group => {
      const listItems = group.querySelectorAll('.facets__item');
      const button = group.querySelector('.show-more');
      const showMoreCount = parseInt(group.dataset.showMoreCount, 10) || 5;
      let expanded = FacetFiltersForm.expandedFilters.has(group.id);

      if (!button) return;

      function updateList() {
        listItems.forEach((item, index) => {
          const shouldCollapse = !expanded && index >= showMoreCount;
          item.classList.toggle('is-collapsed', shouldCollapse);
        });
        button.textContent = expanded ? window.actionStrings.showLess : window.actionStrings.showMore;
      }

      button.addEventListener('click', (event) => {
        event.preventDefault();
        expanded = !expanded;
        if (expanded) {
          FacetFiltersForm.expandedFilters.add(group.id);
        } else {
          FacetFiltersForm.expandedFilters.delete(group.id);
        }
        updateList();
      });

      updateList();

      if (listItems.length <= showMoreCount) {
        button.style.display = 'none';
      }
    });
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();
FacetFiltersForm.expandedFilters = new Set();
FacetFiltersForm.initializeShowMore();

// Afficher le chip sillage en active-facet au chargement si l’URL contient déjà le filtre
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => FacetFiltersForm.injectSillageActiveFacet());
} else {
  FacetFiltersForm.injectSillageActiveFacet();
}

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach(element => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    const allowedKeys = ['Backspace', 'Tab', 'Enter', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Delete', 'Escape'];
    if (!allowedKeys.includes(event.key) && !/[0-9.,]/.test(event.key)) {
      event.preventDefault();
    }
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];

    if (maxInput.value) {
      minInput.setAttribute('data-max', maxInput.value);
    } else {
      minInput.setAttribute('data-max', maxInput.getAttribute('max'));
    }

    if (minInput.value) {
      maxInput.setAttribute('data-min', minInput.value);
    } else {
      maxInput.setAttribute('data-min', minInput.getAttribute('min'));
    }
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min')) || 0; // Default to input's min attribute
    const max = Number(input.getAttribute('max')); // Default to input's max attribute

    if (!isNaN(min) && value < min) input.value = min;
    if (!isNaN(max) && value > max) input.value = max;
  }

  #abortController;

  connectedCallback() {
    this.#abortController = new AbortController();
    const signal = { signal: this.#abortController.signal };

    const priceRangeMin = this.querySelector('input[type="range"]:first-child');
    const priceRangeMax = this.querySelector('input[type="range"]:last-child');

    const priceInputMin = this.querySelector('input[name="filter.v.price.gte"]');
    const priceInputMax = this.querySelector('input[name="filter.v.price.lte"]');

    const minPriceGap = priceRangeMin ? parseInt(priceRangeMin.step) || 1 : 1;
    const rangeGroup = this.querySelector('.range-group');

    if (rangeGroup) {
      rangeGroup.addEventListener('click', (event) => {
        if (event.target.tagName === 'INPUT') return;

        const rect = rangeGroup.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const isRtl = document.documentElement.getAttribute('dir') === 'rtl';
        const percentage = isRtl
          ? 1 - Math.max(0, Math.min(1, x / rect.width))
          : Math.max(0, Math.min(1, x / rect.width));

        if (!priceRangeMin || !priceRangeMax) return;

        const min = parseFloat(priceRangeMin.min);
        const max = parseFloat(priceRangeMin.max);
        const range = max - min;

        const clickedValue = Math.round(min + (range * percentage));

        const currentMin = parseFloat(priceRangeMin.value);
        const currentMax = parseFloat(priceRangeMax.value);

        if (Math.abs(clickedValue - currentMin) < Math.abs(clickedValue - currentMax)) {
          priceRangeMin.value = clickedValue;
          priceRangeMin.dispatchEvent(new Event('input', { bubbles: true }));
          priceRangeMin.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          priceRangeMax.value = clickedValue;
          priceRangeMax.dispatchEvent(new Event('input', { bubbles: true }));
          priceRangeMax.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    const updateRangePosition = (rangeInput, cssProperty) => {
      const percentage = (parseFloat(rangeInput.value) / parseFloat(rangeInput.max)) * 100;
      rangeInput.parentElement.style.setProperty(cssProperty, `${percentage}%`);
    };

    const clamp = (value, min, max) => Math.max(min, Math.min(value, max));

    const validateInput = (input, otherInput, isMin) => {
      const value = parseInt(input.value);
      const absoluteMax = parseInt(input.max);
      const otherValue = parseInt(otherInput.value);

      if (isNaN(value)) return;

      if (isMin) {
        if (value > absoluteMax) {
          input.value = absoluteMax;
        } else if (value >= otherValue) {
          input.value = otherValue - 1;
        }
      } else {
        if (value > absoluteMax) {
          input.value = absoluteMax;
        }
        debounce(() => {
          if (value <= otherValue) {
            input.value = otherValue + 1;
          }
        }, 800);
      }
    };

    priceInputMin.addEventListener('focus', () => priceInputMin.select(), signal);
    priceInputMax.addEventListener('focus', () => priceInputMax.select(), signal);

    priceInputMin.addEventListener('input', () => validateInput(priceInputMin, priceInputMax, true), signal);
    priceInputMax.addEventListener('input', () => validateInput(priceInputMax, priceInputMin, false), signal);

    priceInputMin.addEventListener(
      'change',
      event => {
        event.preventDefault();
        const value = parseInt(event.target.value);
        const maxValue = parseInt(priceInputMax.value || event.target.max);
        const absoluteMin = parseInt(event.target.min);

        event.target.value = clamp(value, absoluteMin, maxValue - minPriceGap);

        if (priceRangeMin) {
          priceRangeMin.value = event.target.value;
          updateRangePosition(priceRangeMin, '--range-min');
        }
        event.target.dispatchEvent(new Event('input', { bubbles: true }));
      },
      signal
    );

    priceInputMax.addEventListener(
      'change',
      event => {
        event.preventDefault();
        const value = parseInt(event.target.value);
        const minValue = parseInt(priceInputMin.value || event.target.min);
        const absoluteMax = parseInt(event.target.max);

        event.target.value = clamp(value, minValue + minPriceGap, absoluteMax);

        if (priceRangeMax) {
          priceRangeMax.value = event.target.value;
          updateRangePosition(priceRangeMax, '--range-max');
        }
        event.target.dispatchEvent(new Event('input', { bubbles: true }));
      },
      signal
    );

    priceRangeMin?.addEventListener(
      'change',
      event => {
        event.stopPropagation();
        priceInputMin.value = event.target.value;
        priceInputMin.dispatchEvent(new Event('change', { bubbles: true }));
      },
      signal
    );

    priceRangeMax?.addEventListener(
      'change',
      event => {
        event.stopPropagation();
        priceInputMax.value = event.target.value;
        priceInputMax.dispatchEvent(new Event('change', { bubbles: true }));
      },
      signal
    );

    priceRangeMin?.addEventListener(
      'input',
      event => {
        const value = parseInt(event.target.value);
        const maxValue = parseInt(priceInputMax.value);

        event.target.value = Math.min(value, maxValue - minPriceGap);

        updateRangePosition(event.target, '--range-min');
        priceInputMin.value = event.target.value;
        priceInputMin.dispatchEvent(new Event('input', { bubbles: true }));
        // Forcer la mise à jour de l’URL via le formulaire parent (ne pas dépendre de la propagation)
        const formEl = this.closest('facet-filters-form');
        if (formEl && typeof formEl.triggerFiltersUpdate === 'function') {
          formEl.triggerFiltersUpdate({ target: priceInputMin, preventDefault: () => {} });
        }
      },
      signal
    );

    priceRangeMax?.addEventListener(
      'input',
      event => {
        const value = parseInt(event.target.value);
        const minValue = parseInt(priceInputMin.value);

        event.target.value = Math.max(value, minValue + minPriceGap);

        updateRangePosition(event.target, '--range-max');
        priceInputMax.value = event.target.value;
        priceInputMax.dispatchEvent(new Event('input', { bubbles: true }));
        // Forcer la mise à jour de l’URL via le formulaire parent (ne pas dépendre de la propagation)
        const formEl = this.closest('facet-filters-form');
        if (formEl && typeof formEl.triggerFiltersUpdate === 'function') {
          formEl.triggerFiltersUpdate({ target: priceInputMax, preventDefault: () => {} });
        }
      },
      signal
    );
  }

  disconnectedCallback() {
    if (this.#abortController) {
      this.#abortController.abort();
    }
  }
}

customElements.define('price-range', PriceRange);

/**
 * Composant range générique pour filtres (ex. sillage) : borne min/max avec sliders et champs numériques.
 * S'appuie sur data-param-gte et data-param-lte pour trouver les inputs (même logique que price-range).
 */
class FacetRange extends HTMLElement {
  #abortController;

  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    const allowedKeys = ['Backspace', 'Tab', 'Enter', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Delete', 'Escape'];
    if (!allowedKeys.includes(event.key) && !/[0-9.,]/.test(event.key)) {
      event.preventDefault();
    }
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input[type="number"]');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (!minInput || !maxInput) return;
    if (maxInput.value) {
      minInput.setAttribute('data-max', maxInput.value);
    } else {
      minInput.setAttribute('data-max', maxInput.getAttribute('max'));
    }
    if (minInput.value) {
      maxInput.setAttribute('data-min', minInput.value);
    } else {
      maxInput.setAttribute('data-min', minInput.getAttribute('min'));
    }
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min')) || 0;
    const max = Number(input.getAttribute('max'));
    if (!isNaN(min) && value < min) input.value = min;
    if (!isNaN(max) && value > max) input.value = max;
  }

  /**
   * Restaure les valeurs min/max depuis l’URL (ex. chargement avec ?filter.p.m.custom.sillage.gte=10&filter.p.m.custom.sillage.lte=45).
   * Met à jour les champs numériques, les range inputs et les variables CSS du slider.
   */
  _restoreFromUrl(paramGte, paramLte, priceRangeMin, priceRangeMax, priceInputMin, priceInputMax) {
    const params = new URLSearchParams(window.location.search);
    const gte = params.get(paramGte);
    const lte = params.get(paramLte);
    if (gte === null || lte === null) return;
    const minVal = parseFloat(gte);
    const maxVal = parseFloat(lte);
    if (Number.isNaN(minVal) || Number.isNaN(maxVal)) return;
    const rangeMin = priceRangeMin ? parseFloat(priceRangeMin.getAttribute('min')) : 0;
    const rangeMax = priceRangeMax ? parseFloat(priceRangeMax.getAttribute('max')) : 100;
    const span = rangeMax - rangeMin;
    if (span <= 0) return;
    const clampedMin = Math.max(rangeMin, Math.min(minVal, rangeMax));
    const clampedMax = Math.max(rangeMin, Math.min(maxVal, rangeMax));
    if (priceInputMin) priceInputMin.value = clampedMin;
    if (priceInputMax) priceInputMax.value = clampedMax;
    if (priceRangeMin) priceRangeMin.value = clampedMin;
    if (priceRangeMax) priceRangeMax.value = clampedMax;
    const rangeGroup = this.querySelector('.range-group');
    if (rangeGroup) {
      const minPct = ((clampedMin - rangeMin) / span) * 100;
      const maxPct = ((clampedMax - rangeMin) / span) * 100;
      rangeGroup.style.setProperty('--range-min', `${minPct}%`);
      rangeGroup.style.setProperty('--range-max', `${maxPct}%`);
    }
  }

  connectedCallback() {
    this.#abortController = new AbortController();
    const signal = { signal: this.#abortController.signal };
    const paramGte = this.getAttribute('data-param-gte');
    const paramLte = this.getAttribute('data-param-lte');
    if (!paramGte || !paramLte) return;

    const priceRangeMin = this.querySelector('input[type="range"]:first-of-type');
    const priceRangeMax = this.querySelector('input[type="range"]:last-of-type');
    const priceInputMin = this.querySelector(`input[name="${paramGte}"]`);
    const priceInputMax = this.querySelector(`input[name="${paramLte}"]`);
    if (!priceInputMin || !priceInputMax) return;

    // Au chargement, restaurer les valeurs depuis l’URL si présentes (ex. retour arrière ou lien avec filtres)
    this._restoreFromUrl(paramGte, paramLte, priceRangeMin, priceRangeMax, priceInputMin, priceInputMax);

    const minPriceGap = priceRangeMin ? parseInt(priceRangeMin.step) || 1 : 1;
    const rangeGroup = this.querySelector('.range-group');

    if (rangeGroup && priceRangeMin && priceRangeMax) {
      rangeGroup.addEventListener(
        'click',
        (event) => {
          if (event.target.tagName === 'INPUT') return;
          const rect = rangeGroup.getBoundingClientRect();
          const x = event.clientX - rect.left;
          const isRtl = document.documentElement.getAttribute('dir') === 'rtl';
          const percentage = isRtl ? 1 - Math.max(0, Math.min(1, x / rect.width)) : Math.max(0, Math.min(1, x / rect.width));
          const min = parseFloat(priceRangeMin.min);
          const max = parseFloat(priceRangeMin.max);
          const range = max - min;
          const clickedValue = Math.round(min + range * percentage);
          const currentMin = parseFloat(priceRangeMin.value);
          const currentMax = parseFloat(priceRangeMax.value);
          if (Math.abs(clickedValue - currentMin) < Math.abs(clickedValue - currentMax)) {
            priceRangeMin.value = clickedValue;
            priceRangeMin.dispatchEvent(new Event('input', { bubbles: true }));
            priceRangeMin.dispatchEvent(new Event('change', { bubbles: true }));
          } else {
            priceRangeMax.value = clickedValue;
            priceRangeMax.dispatchEvent(new Event('input', { bubbles: true }));
            priceRangeMax.dispatchEvent(new Event('change', { bubbles: true }));
          }
        },
        signal
      );
    }

    // Pour un range avec min !== 0 (ex. sillage), le pourcentage doit être (value - min) / (max - min)
    const updateRangePosition = (rangeInput, cssProperty) => {
      if (!rangeInput || !rangeInput.parentElement) return;
      const value = parseFloat(rangeInput.value);
      const min = parseFloat(rangeInput.getAttribute('min')) || 0;
      const max = parseFloat(rangeInput.getAttribute('max')) || 100;
      const rangeSpan = max - min;
      const percentage = rangeSpan > 0 ? ((value - min) / rangeSpan) * 100 : 0;
      rangeInput.parentElement.style.setProperty(cssProperty, `${percentage}%`);
    };

    const clamp = (value, min, max) => Math.max(min, Math.min(value, max));

    const validateInput = (input, otherInput, isMin) => {
      const value = parseInt(input.value, 10);
      const absoluteMax = parseInt(input.getAttribute('max'), 10);
      const otherValue = parseInt(otherInput.value, 10);
      if (isNaN(value)) return;
      if (isMin) {
        if (value > absoluteMax) input.value = absoluteMax;
        else if (value >= otherValue) input.value = otherValue - 1;
      } else {
        if (value > absoluteMax) input.value = absoluteMax;
        debounce(() => {
          if (value <= otherValue) input.value = otherValue + 1;
        }, 800);
      }
    };

    priceInputMin.addEventListener('focus', () => priceInputMin.select(), signal);
    priceInputMax.addEventListener('focus', () => priceInputMax.select(), signal);
    priceInputMin.addEventListener('input', () => validateInput(priceInputMin, priceInputMax, true), signal);
    priceInputMax.addEventListener('input', () => validateInput(priceInputMax, priceInputMin, false), signal);

    priceInputMin.addEventListener(
      'change',
      (event) => {
        event.preventDefault();
        const value = parseInt(event.target.value, 10);
        const maxValue = parseInt(priceInputMax.value || event.target.getAttribute('max'), 10);
        const absoluteMin = parseInt(event.target.getAttribute('min'), 10);
        event.target.value = clamp(value, absoluteMin, maxValue - minPriceGap);
        if (priceRangeMin) {
          priceRangeMin.value = event.target.value;
          updateRangePosition(priceRangeMin, '--range-min');
        }
        event.target.dispatchEvent(new Event('input', { bubbles: true }));
      },
      signal
    );

    priceInputMax.addEventListener(
      'change',
      (event) => {
        event.preventDefault();
        const value = parseInt(event.target.value, 10);
        const minValue = parseInt(priceInputMin.value || event.target.getAttribute('min'), 10);
        const absoluteMax = parseInt(event.target.getAttribute('max'), 10);
        event.target.value = clamp(value, minValue + minPriceGap, absoluteMax);
        if (priceRangeMax) {
          priceRangeMax.value = event.target.value;
          updateRangePosition(priceRangeMax, '--range-max');
        }
        event.target.dispatchEvent(new Event('input', { bubbles: true }));
      },
      signal
    );

    priceRangeMin?.addEventListener(
      'change',
      (event) => {
        event.stopPropagation();
        priceInputMin.value = event.target.value;
        priceInputMin.dispatchEvent(new Event('change', { bubbles: true }));
      },
      signal
    );

    priceRangeMax?.addEventListener(
      'change',
      (event) => {
        event.stopPropagation();
        priceInputMax.value = event.target.value;
        priceInputMax.dispatchEvent(new Event('change', { bubbles: true }));
      },
      signal
    );

    priceRangeMin?.addEventListener(
      'input',
      (event) => {
        const value = parseInt(event.target.value, 10);
        const maxValue = parseInt(priceInputMax.value, 10);
        event.target.value = Math.min(value, maxValue - minPriceGap);
        updateRangePosition(event.target, '--range-min');
        priceInputMin.value = event.target.value;
      },
      signal
    );

    priceRangeMax?.addEventListener(
      'input',
      (event) => {
        const value = parseInt(event.target.value, 10);
        const minValue = parseInt(priceInputMin.value, 10);
        event.target.value = Math.max(value, minValue + minPriceGap);
        updateRangePosition(event.target, '--range-max');
        priceInputMax.value = event.target.value;
      },
      signal
    );
  }

  disconnectedCallback() {
    if (this.#abortController) this.#abortController.abort();
  }
}

customElements.define('facet-range', FacetRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', event => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
